import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useStoreActions, useStoreState } from "easy-peasy";

import PortfolioSelect from "components/Forms/PortfolioSelect/PortfolioSelect";
import "./SinglePortfolioViewSelect.css";

/**
 * "Näytettävä salkku" for views that show one portfolio at a time (#1409): derivatives,
 * securities, real estate, imports and the front page's "Valittu salkku" card.
 *
 * Rendered only when several portfolios are active; with one the view shows it as before.
 * The options are the active portfolios and the value is the target portfolio, which these
 * views and the forms inside them already read. Changing it sets the target only: the active
 * selection in the top-left selector stays as it is.
 */
const SinglePortfolioViewSelect = ({ id = "single-portfolio-view-select", className = "" }) => {
  const { t } = useTranslation();
  const activePortfolios = useStoreState((state) => state.portfolio?.activePortfolios) || [];
  const portfolio = useStoreState((state) => state.portfolio?.portfolio);
  const setPortfolio = useStoreActions((actions) => actions.portfolio.setPortfolio);

  const several = activePortfolios.length > 1;
  const shown = activePortfolios.find((p) => p.id === portfolio?.id);
  const firstActive = activePortfolios[0];

  // An edited row can leave a target outside the active portfolios; this view then shows the
  // first active one, so what it shows is always one of the options.
  useEffect(() => {
    if (several && !shown && firstActive) {
      setPortfolio(firstActive);
    }
  }, [several, shown, firstActive, setPortfolio]);

  if (!several) {
    return null;
  }

  const label = t("single_portfolio_view_label", "Näytettävä salkku");
  const noteId = `${id}-note`;

  const handleChange = ({ target }) => {
    const next = activePortfolios.find((p) => p.id === Number(target.value));
    if (next) {
      setPortfolio(next);
    }
  };

  return (
    <div className={`single-portfolio-view-select ${className}`}>
      <div className="single-portfolio-view-select__control">
        <PortfolioSelect
          id={id}
          name="single_portfolio_view"
          label={label}
          aria-label={label}
          aria-describedby={noteId}
          portfolios={activePortfolios}
          value={shown?.id ?? firstActive?.id}
          onChange={handleChange}
        />
      </div>
      <p id={noteId} className="single-portfolio-view-select__note">
        <i className="fas fa-info-circle mr-2" aria-hidden="true" />
        {t("single_portfolio_view_note", "Tämä näkymä näyttää yhden salkun kerrallaan. Aktiivisia salkkuja on {{count}}.", {
          count: activePortfolios.length,
        })}
      </p>
    </div>
  );
};

export default SinglePortfolioViewSelect;
